function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { PureComponent } from "react";
import Head from "next/head";
import { GenericConfigContext } from "contexts/generic-config-context";
import { AbTestsContext } from "contexts/ab-tests-context";
import BaseHeaderComponent from "segments/desktop/header/layouts/base-header/base-header-component";
import LongFooterComponent from "segments/desktop/footer/layouts/long-footer/long-footer-component";
import SlimFooterComponent from "segments/desktop/footer/layouts/slim-footer/slim-footer-component";
import LlmsTxtLinkComponent from "components/core/head/llms-txt-link-component";
import { setDefaultBigBrainSource } from "services/bigbrain-service";
import { GENERATED_PAGES_SOURCE } from "constants/bigbrain-event-types";
import { buildClusterScriptBody } from "server/routes/generated-pages/cluster-tag";
import { CUSTOM_FLOW_VALUE_PROPERTY_NAME } from "server/routes/generated-pages/custom-signup-flow";
import { parseCustomCodeElements } from "server/routes/generated-pages/custom-code-tags";
const noop = ()=>{};
// When a localeId is set, Generator fetches headerCatalogLinks from Redis and renders the full
// nav dropdown. The nav uses .button (div wrapper, zero styled-component CSS) and .title (section
// labels). AI pages ship their own CSS with generic class names that accidentally target these
// elements — .button rules make the signup wrapper huge/colored, .title rules hide dropdown labels.
// Without localeId the catalog links are empty, the nav dropdown doesn't render, so no conflict.
const HEADER_CSS_ISOLATION = `
  .base-header-component .button {
    background-color: transparent;
    border-radius: 0;
    flex: unset;
    justify-content: unset;
    align-items: unset;
    text-align: unset;
    padding: 0;
  }
  .base-header-component .title {
    color: inherit;
    visibility: visible;
    opacity: 1;
  }
`;
// Mirrors base-template's translate helpers, built from the serialized translations dict so the
// header/footer resolve their labels (on both server render and client hydration).
const buildTranslate = (translations = {})=>{
    const translate = (key)=>translations[key] || key;
    const translateWithParam = (key, param1)=>translate(key).replace("%param1%", param1);
    const translateOrTranslateWithParam = (keyToTranslate)=>{
        if (!keyToTranslate) return null;
        if (typeof keyToTranslate === "object") return translateWithParam(keyToTranslate.key, keyToTranslate.param);
        return translate(keyToTranslate);
    };
    return {
        translate,
        translateWithParam,
        translateOrTranslateWithParam
    };
};
// headCustomCode/bodyCustomCode are validated (generated-pages-route.js) to be nothing but bare
// <meta>/<link> tags — parseCustomCodeElements (custom-code-tags.js) turns the stored string into
// [{ tagName, attributes }] so they can render as real elements here, since Next's <Head> needs
// actual elements rather than a raw HTML string (unlike the raw-passthrough render path, which
// splices the string directly — see applyCustomCodeTags in that same file).
const renderCustomCodeTags = (elements)=>elements.map(({ tagName, attributes }, i)=>{
        const Tag = tagName;
        return /*#__PURE__*/ _jsx(Tag, _object_spread({}, attributes), i);
    });
class AiGeneratedTemplate extends PureComponent {
    // The /ap/ BODY bundle is injected here (client-side, post-hydration) rather than as SSR <script>
    // children. As SSR markup it ran during initial parse — BEFORE React hydrated — and mutated the DOM
    // the header/footer render into (rewriting signup/footer hrefs, injecting spinners/CSS). React then
    // rebuilt those nodes on hydration, orphaning the click listeners the bundle had already attached, so
    // events like the signup button's `signup_main_cta_clicked` never fired. Injecting after mount lets the
    // bundle bind to the final, hydrated DOM, which restored those events.
    // async=false preserves execution order (homepage-shared body must run before ap body — it defines
    // window.BigBrain, which ap body uses). The data-ap-body-script marker keeps it idempotent so a
    // StrictMode remount doesn't double-load the bundle.
    componentDidMount() {
        const { apBodyScripts = [] } = this.props;
        // Attribute the reused Generator header/footer's BigBrain events to the generated page rather than
        // the homepage (default HP_SOURCE). Covers every trackEvent()-based header/footer event; events
        // fired via window.BigBrain('track') directly keep the /ap/ bundle's own default. Set here (not in
        // render) so it only takes effect client-side, where trackEvent actually emits.
        setDefaultBigBrainSource(GENERATED_PAGES_SOURCE);
        apBodyScripts.forEach((src)=>{
            if (document.querySelector(`script[data-ap-body-script="${src}"]`)) return;
            const script = document.createElement("script");
            script.src = src;
            script.async = false;
            script.dataset.apBodyScript = src;
            document.body.appendChild(script);
        });
    }
    componentWillUnmount() {
        // Reset so a client-side (SPA) navigation away from a /gp/ page doesn't leak the generated_pages
        // source onto a subsequently-rendered homepage page sharing this module instance.
        setDefaultBigBrainSource();
    }
    render() {
        const { pageConfig = {}, aiBodyHtml, aiHeadStylesheetHrefs, aiHeadInlineStyles, apHeadScripts = [], translations, footerCatalogLinks, headerCatalogLinks, abTestVariants, abTestExactEventName, crossOriginLocaleAbTest, isMobileOrTabletBrowser, isMobileBrowser, persistantCookies, userSelectedLanguage } = this.props;
        const isLong = pageConfig.addHeaderAndFooter === "long";
        const Footer = isLong ? LongFooterComponent : SlimFooterComponent;
        // Generic-config the header/footer consume (see base-template.js for the full shape).
        // setStartShowingStickyHeader must be a function — long-footer calls it in an effect.
        // persistantCookies is read by structured-header to determine isExistingUser (affects
        // which CTA button renders). userSelectedLanguage seeds the language-picker's initial state.
        // customSignupFlow mirrors base-template.js — read by SignupCoreComponent (withGenericConfig)
        // to append the custom_flow query param to the React-rendered header's signup button, same as
        // regular Generator pages. The page body (aiBodyHtml) isn't React — its signup buttons get the
        // same value via the /ap/ bundle's window.pageConfig mechanism instead (custom-signup-flow.js).
        const genericConfig = _object_spread_props(_object_spread({}, buildTranslate(translations)), {
            localeId: pageConfig.localeId,
            footerCatalogLinks: footerCatalogLinks || {},
            headerCatalogLinks: headerCatalogLinks || {},
            isSecondaryMenuNeeded: false,
            isMobileOrTabletBrowser: !!isMobileOrTabletBrowser,
            isMobileBrowser: !!isMobileBrowser,
            setStartShowingStickyHeader: noop,
            setIsFirstFoldVisible: noop,
            persistantCookies,
            userSelectedLanguage,
            customSignupFlow: pageConfig.customSignupFlow
        });
        const { title, description, canonicalUrl, ogImagePath, twitterImagePath, jsonLdSchema, noIndex, noFollow, headCustomCode, bodyCustomCode } = pageConfig;
        const robotsContent = noIndex || noFollow ? `${noIndex ? "noindex" : "index"}, ${noFollow ? "nofollow" : "follow"}` : null;
        const clusterScriptBody = buildClusterScriptBody(pageConfig);
        const headCustomCodeElements = parseCustomCodeElements(headCustomCode);
        const bodyCustomCodeElements = parseCustomCodeElements(bodyCustomCode);
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs(Head, {
                    children: [
                        (aiHeadStylesheetHrefs === null || aiHeadStylesheetHrefs === void 0 ? void 0 : aiHeadStylesheetHrefs.length) > 0 && /*#__PURE__*/ _jsx("style", {
                            dangerouslySetInnerHTML: {
                                __html: "@layer normalize, global;"
                            }
                        }),
                        aiHeadStylesheetHrefs === null || aiHeadStylesheetHrefs === void 0 ? void 0 : aiHeadStylesheetHrefs.map((href, i)=>/*#__PURE__*/ _jsx("link", {
                                rel: "stylesheet",
                                href: href
                            }, i)),
                        aiHeadInlineStyles && /*#__PURE__*/ _jsx("style", {
                            dangerouslySetInnerHTML: {
                                __html: aiHeadInlineStyles
                            }
                        }),
                        pageConfig.localeId && pageConfig.addHeaderAndFooter && /*#__PURE__*/ _jsx("style", {
                            dangerouslySetInnerHTML: {
                                __html: HEADER_CSS_ISOLATION
                            }
                        }),
                        pageConfig.customSignupFlow && /*#__PURE__*/ _jsx("script", {
                            dangerouslySetInnerHTML: {
                                __html: `window.pageConfig = Object.assign({}, window.pageConfig, { "${CUSTOM_FLOW_VALUE_PROPERTY_NAME}": ${JSON.stringify(String(pageConfig.customSignupFlow)).replace(/<\/script/gi, "<\\/script")} });`
                            }
                        }),
                        apHeadScripts.map((src)=>/*#__PURE__*/ _jsx("script", {
                                src: src
                            }, src)),
                        LlmsTxtLinkComponent(),
                        title && /*#__PURE__*/ _jsx("title", {
                            children: title
                        }),
                        title && /*#__PURE__*/ _jsx("meta", {
                            property: "og:title",
                            content: title
                        }),
                        title && /*#__PURE__*/ _jsx("meta", {
                            name: "twitter:title",
                            content: title
                        }),
                        description && /*#__PURE__*/ _jsx("meta", {
                            name: "description",
                            content: description
                        }),
                        description && /*#__PURE__*/ _jsx("meta", {
                            property: "og:description",
                            content: description
                        }),
                        description && /*#__PURE__*/ _jsx("meta", {
                            name: "twitter:description",
                            content: description
                        }),
                        robotsContent && /*#__PURE__*/ _jsx("meta", {
                            name: "robots",
                            content: robotsContent
                        }),
                        canonicalUrl && /*#__PURE__*/ _jsx("link", {
                            rel: "canonical",
                            href: canonicalUrl
                        }),
                        ogImagePath && /*#__PURE__*/ _jsx("meta", {
                            property: "og:image",
                            content: ogImagePath
                        }),
                        twitterImagePath && /*#__PURE__*/ _jsx("meta", {
                            name: "twitter:card",
                            content: "summary"
                        }),
                        twitterImagePath && /*#__PURE__*/ _jsx("meta", {
                            name: "twitter:image",
                            content: twitterImagePath
                        }),
                        jsonLdSchema && /*#__PURE__*/ _jsx("script", {
                            type: "application/ld+json",
                            dangerouslySetInnerHTML: {
                                __html: String(jsonLdSchema).replace(/<\/script/gi, "<\\/script")
                            }
                        }),
                        renderCustomCodeTags(headCustomCodeElements),
                        clusterScriptBody && /*#__PURE__*/ _jsx("script", {
                            dangerouslySetInnerHTML: {
                                __html: clusterScriptBody
                            }
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(GenericConfigContext.Provider, {
                    value: genericConfig,
                    children: /*#__PURE__*/ _jsxs(AbTestsContext.Provider, {
                        value: {
                            abTestVariants,
                            abTestExactEventName,
                            crossOriginLocaleAbTest
                        },
                        children: [
                            /*#__PURE__*/ _jsx(BaseHeaderComponent, {
                                showMenu: isLong
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "ai-generated-content",
                                dangerouslySetInnerHTML: {
                                    __html: aiBodyHtml || ""
                                }
                            }),
                            renderCustomCodeTags(bodyCustomCodeElements),
                            /*#__PURE__*/ _jsx(Footer, {})
                        ]
                    })
                })
            ]
        });
    }
}
// Renders an AI-generated page's body between the Generator's React header + footer WITHOUT the full
// BaseTemplate — so the page's own /ap/ bundle stays the single source of scripts/analytics (incl.
// BigBrain). We supply only the React contexts the header/footer read and re-load the /ap/ bundle:
// the HEAD half in the Next-built <head> (early init — safe there since the head bundle doesn't query
// the page body), and the BODY half imperatively AFTER hydration (see componentDidMount).
// SEO tags mirror applySeoTags (seo-tags.js) — same fields, same logic — but via next/head so Next
// owns the <head> and deduplicates tags from any child component.
// `addHeaderAndFooter`: "long" = full nav header + long-footer; anything else here = minimal header + slim-footer.
export { AiGeneratedTemplate as default };
